import { c as _c } from "react/compiler-runtime";
import { forwardRef, type ComponentPropsWithoutRef, type ComponentType } from "react";
import { PanelLeft, type LucideIcon } from "lucide-react";
import { cn } from "@repo/blacksmith-ui/cn";
import { Button } from "@repo/blacksmith-ui/button";
export type HeaderIconButtonSize = "sm" | "md";
const SIZE_CLASS: Record<HeaderIconButtonSize, string> = {
  sm: "size-7",
  md: "size-8"
};
const BASE_CLASS = "flex shrink-0 items-center justify-center rounded-lg text-neutral-600 dark:text-neutral-400";
const ICON_CLASS = "size-4";
interface HeaderIconButtonProps extends Omit<ComponentPropsWithoutRef<"button">, "children"> {
  icon: ComponentType<{
    className?: string;
  }>;
  /** `sm` (28px) is the Codesmith pane control; `md` (32px) the mobile one. */
  size?: HeaderIconButtonSize;
  "aria-label": string;
}

/** Flat 16px-glyph icon button for a header row. */
export const HeaderIconButton = forwardRef<HTMLButtonElement, HeaderIconButtonProps>(function HeaderIconButton(t0, ref) {
  const $ = _c(17);
  let Icon;
  let className;
  let rest;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      icon: Icon,
      size: t1,
      className,
      type: t2,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = Icon;
    $[2] = className;
    $[3] = rest;
    $[4] = t1;
    $[5] = t2;
  } else {
    Icon = $[1];
    className = $[2];
    rest = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const size = t1 === undefined ? "sm" : t1;
  const type = t2 === undefined ? "button" : t2;
  const t3 = SIZE_CLASS[size];
  let t4;
  if ($[6] !== className || $[7] !== t3) {
    t4 = cn(t3, className);
    $[6] = className;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== Icon) {
    t5 = <Icon className={ICON_CLASS} />;
    $[9] = Icon;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== ref || $[12] !== rest || $[13] !== t4 || $[14] !== t5 || $[15] !== type) {
    t6 = <Button ref={ref} type={type} variant="header-icon" className={t4} {...rest}>{t5}</Button>;
    $[11] = ref;
    $[12] = rest;
    $[13] = t4;
    $[14] = t5;
    $[15] = type;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
});

/** Non-interactive twin of HeaderIconButton for skeletons. */
export function HeaderIconPlaceholder(t0) {
  const $ = _c(8);
  const {
    icon: Icon,
    size: t1,
    className
  } = t0;
  const size = t1 === undefined ? "sm" : t1;
  const t2 = SIZE_CLASS[size];
  let t3;
  if ($[0] !== className || $[1] !== t2) {
    t3 = cn(BASE_CLASS, t2, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== Icon) {
    t4 = <Icon className={ICON_CLASS} aria-hidden={true} />;
    $[3] = Icon;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = <div className={t3}>{t4}</div>;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
interface PanelToggleButtonProps extends Omit<ComponentPropsWithoutRef<"button">, "children" | "aria-label"> {
  /** Whether the panel this button controls is currently collapsed. */
  collapsed: boolean;
  size?: HeaderIconButtonSize;
}

/** The ⌥B hint matches the shortcut the session view binds. */
export const PanelToggleButton = forwardRef<HTMLButtonElement, PanelToggleButtonProps>(function PanelToggleButton(t0, ref) {
  const $ = _c(9);
  let collapsed;
  let rest;
  let size;
  if ($[0] !== t0) {
    ({
      collapsed,
      size,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = collapsed;
    $[2] = rest;
    $[3] = size;
  } else {
    collapsed = $[1];
    rest = $[2];
    size = $[3];
  }
  const t1 = collapsed ? "Expand sidebar (\u2325B)" : "Collapse sidebar (\u2325B)";
  let t2;
  if ($[4] !== ref || $[5] !== rest || $[6] !== size || $[7] !== t1) {
    t2 = <HeaderIconButton ref={ref} icon={PanelLeft} size={size} aria-label={t1} {...rest} />;
    $[4] = ref;
    $[5] = rest;
    $[6] = size;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
export function PanelTogglePlaceholder(t0) {
  const $ = _c(3);
  const {
    size,
    className
  } = t0;
  let t1;
  if ($[0] !== className || $[1] !== size) {
    t1 = <HeaderIconPlaceholder icon={PanelLeft} size={size} className={className} />;
    $[0] = className;
    $[1] = size;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}